import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import './employee.css';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Select, MenuItem, InputLabel, FormControl, CircularProgress, Box, Tabs, Tab
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import ImportEmployees from './ImportEmployees';

function Employee({ employeeRole = "employee", refresh, onViewClick }) {
  const [employees, setEmployees] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', designation_id: '', department_id: '', manager_id: '',
    role: '', joining_date: '', relieving_date: '', password: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

  const navigate = useNavigate();

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [employeesRes, designationsRes, departmentsRes] = await Promise.all([
        api.get(`/employees${tab === 'trashed' ? '?trashed=true' : ''}`),
        api.get('/designations'),
        api.get('/departments')
      ]);

      let filteredEmployees = employeesRes.data;

      // For regular employee, show only their own data
      if (employeeRole === 'employee') {
        filteredEmployees = filteredEmployees.slice(0, 1);
      }

      const processed = filteredEmployees.map(emp => {
        const matchedDesignation = designationsRes.data.find(d => String(d.id) === String(emp.designation_id));
        const matchedDepartment = departmentsRes.data.find(d => String(d.id) === String(emp.department_id));
        return {
          ...emp,
          id: emp.id || emp._id,
          designation: matchedDesignation || { name: 'Unassigned' },
          department: matchedDepartment || { name: 'Unassigned' },
          manager: filteredEmployees.find(m => String(m.id) === String(emp.manager_id)) || null
        };
      });

      setEmployees(processed);
      setDesignations(designationsRes.data);
      setDepartments(departmentsRes.data);
      setManagers(processed.filter(emp => emp.role === "manager"));
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [tab, employeeRole]);

  useEffect(() => { fetchAllData(); }, [fetchAllData, refresh, tab]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const openModal = () => {
    setFormData({
      name: '', email: '', phone: '', designation_id: '', department_id: '', manager_id: '',
      role: '', joining_date: '', relieving_date: '', password: ''
    });
    setEditingId(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingAction(true);
    try {
      const payload = { ...formData };
      if (!editingId && !formData.password) {
        alert("Password is required");
        setLoadingAction(false);
        return;
      }
      editingId
        ? await api.put(`/employees/${editingId}`, payload)
        : await api.post('/employees', payload);

      setModalOpen(false);
      fetchAllData();
    } catch (err) {
      alert('Error submitting');
      console.error(err);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEdit = (emp) => {
    setFormData({
      name: emp.name || '', email: emp.email || '', phone: emp.phone || '',
      designation_id: emp.designation_id || '', department_id: emp.department_id || '',
      manager_id: emp.manager_id || '', role: emp.role || '',
      joining_date: emp.joining_date || '', relieving_date: emp.relieving_date || '', password: ''
    });
    setEditingId(emp.id);
    setModalOpen(true);
  };

  const handleDelete = async (id) => { await api.delete(`/employees/${id}`); fetchAllData(); };
  const handleRestore = async (id) => { await api.post(`/employees/${id}/restore`); fetchAllData(); };
  const handleForceDelete = async (id) => { await api.delete(`/employees/${id}/force-delete`); fetchAllData(); };

  const handleDownloadLetter = async (id, type) => {
    try {
      const response = await api.get(`/letter/${type}/${id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_letter_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${type} letter.`);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 130 },
    { field: 'designation', headerName: 'Designation', width: 150,
      renderCell: (params) => params?.row?.designation?.name || 'Unassigned' },
    { field: 'department', headerName: 'Department', width: 150,
      renderCell: (params) => params?.row?.department?.name || 'Unassigned' },
    { field: 'role', headerName: 'Role', width: 120 },
    { field: 'joining_date', headerName: 'Joining Date', width: 130 },
    { field: 'relieving_date', headerName: 'Relieving Date', width: 130 },
    { field: 'actions', headerName: 'Actions', width: 380,
      renderCell: (params) => {
        const row = params.row;
        const isEmployee = employeeRole === 'employee';
        const isHRorSuper_Admin = employeeRole === 'hr' || employeeRole === 'super_admin';
        return (
          <div className="action-buttons">
            {tab === 'active' ? (
              <>
                <Button size="small" onClick={() => navigate(`/employee/${row.id}/assign`)}>Assign</Button>
                {(isEmployee || isHRorSuper_Admin) && (
                  <>
                    <Button size="small" onClick={() => handleEdit(row)}>Edit</Button>
                    <Button size="small" onClick={() => handleDelete(row.id)}>Delete</Button>
                  </>
                )}
                {isHRorSuper_Admin && (
                  <FormControl size="small" style={{ minWidth: 140 }}>
                    <Select
                      displayEmpty
                      value=""
                      onChange={(e) => handleDownloadLetter(row.id, e.target.value)}
                      renderValue={() => "PDF"}
                    >
                      <MenuItem value="experience">Experience Letter</MenuItem>
                      <MenuItem value="offer">Offer Letter</MenuItem>
                      <MenuItem value="increment">Increment Letter</MenuItem>
                      <MenuItem value="internship">Internship Letter</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            ) : (
              <>
                {isHRorSuper_Admin && (
                  <>
                    <Button size="small" onClick={() => handleRestore(row.id)}>Restore</Button>
                    <Button size="small" onClick={() => handleForceDelete(row.id)}>Delete Permanently</Button>
                  </>
                )}
              </>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="table-scroll-container">
      <div className="mui-employee-container">
        {loading && <div className="employee-loader-overlay"><CircularProgress /></div>}
        {loadingAction && <Box className="overlay-loader"><CircularProgress /></Box>}

        <h2>{employeeRole === 'employee' ? 'My Profile' : 'Employee Management'}</h2>

        <div className="employee-section-box">
          <Tabs value={tab} onChange={(e, val) => setTab(val)}>
            <Tab value="active" label="Active Employees" />
            {(employeeRole === 'super_admin' || employeeRole === 'hr') && (
              <Tab value="trashed" label="Trashed Employees" />
            )}
          </Tabs>

          {(employeeRole === 'super_admin' || employeeRole === 'hr') && (
            <div className="mui-toolbar">
              <Button variant="contained" onClick={openModal} className="add-employee-btn">Add Employee</Button>
              <ImportEmployees onImportDone={fetchAllData} />
            </div>
          )}

          <div className="tab-wrapper">
            <div className="employee-table-container">
              <DataGrid
                rows={employees}
                columns={columns}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => setPaginationModel(model)}
                rowsPerPageOptions={[10, 20, 50, 100]}
                getRowId={(row) => row.id || row._id}
                autoHeight={false}
                disableExtendRowFullWidth
                sx={{
                  width: "100%",
                  minWidth: "1000px",
                  height: "100%",
                  "& .MuiDataGrid-virtualScroller": { overflowX: "auto !important" },
                }}
              />
            </div>
          </div>

          <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
            <DialogTitle>{editingId ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <form onSubmit={handleSubmit}>
              <DialogContent dividers className="employee-form" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="form-row">
                  <TextField label="Name" name="name" value={formData.name} onChange={handleChange} required className="form-col" />
                  <TextField label="Email" name="email" value={formData.email} onChange={handleChange} required className="form-col" />
                </div>
                <div className="form-row">
                  <TextField label="Phone" name="phone" value={formData.phone} onChange={handleChange} required className="form-col" />
                  <FormControl className="form-col">
                    <InputLabel>Role</InputLabel>
                    <Select name="role" value={formData.role} onChange={handleChange} required>
                      <MenuItem value="admin">Super Admin</MenuItem>
                      <MenuItem value="hr">HR</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="form-row">
                  <FormControl className="form-col">
                    <InputLabel>Designation</InputLabel>
                    <Select name="designation_id" value={formData.designation_id} onChange={handleChange} required>
                      {designations.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl className="form-col">
                    <InputLabel>Department</InputLabel>
                    <Select name="department_id" value={formData.department_id} onChange={handleChange}>
                      {departments.map(dep => <MenuItem key={dep.id} value={dep.id}>{dep.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
                <div className="form-row">
                  <FormControl className="form-col">
                    <InputLabel>Manager</InputLabel>
                    <Select name="manager_id" value={formData.manager_id} onChange={handleChange}>
                      {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
                <div className="form-row">
                  <TextField type="date" name="joining_date" label="Joining Date" value={formData.joining_date} onChange={handleChange} InputLabelProps={{ shrink: true }} className="form-col" />
                  <TextField type="date" name="relieving_date" label="Relieving Date" value={formData.relieving_date} onChange={handleChange} InputLabelProps={{ shrink: true }} className="form-col" />
                </div>
                {!editingId && (
                  <div className="form-row">
                    <TextField label="Password" name="password" type="password" value={formData.password} onChange={handleChange} required className="form-col" />
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={loadingAction}>{editingId ? 'Update' : 'Add'}</Button>
              </DialogActions>
            </form>
          </Dialog>

        </div>
      </div>
    </div>
  );
}

export default Employee;
